import {
  createListenerMiddleware,
  type AnyAction,
  type Dispatch,
} from "@reduxjs/toolkit";
import type { RootState } from "./Store";
import {
  applyUserPreferences,
  BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY,
  setAutoSave,
  setAutoConnectToUnity,
  setBrowseDialogViewMode,
  setShowEnvironmentProps,
  setShowCameraWidget,
  setThemeMode,
} from "./slices/GlobalSlice";
import {
  setAuthenticationSuccess,
  setLogout,
  setReauthenticationSuccess,
} from "./slices/AuthenticationSlice";
import {
  setCurrentOrganization,
  setOrganizations,
} from "./slices/UserSlice";
import {
  clearUserSettings,
  setUserSettingsSaveFailed,
  setUserSettingsSaveSucceeded,
  setUserSettingsSaving,
} from "./slices/UserSettingsSlice";
import {
  selectOnboardingProgress,
  setHintDismissed,
  setOnboardingCleared,
  setOnboardingReset,
  setTourAbandoned,
  setTourCompleted,
  setWelcomeChoice,
} from "./slices/OnboardingSlice";
import { UserSettingsService } from "../services/UserSettingsService";
import type { UserSettings } from "../services/UserSettingsService";

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage errors so state updates remain deterministic.
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors so state updates remain deterministic.
  }
}

/** Legacy key retained only so the server-backed rollout can discard it. */
export function legacyOnboardingStorageKey(userID: string): string {
  return `onboarding:${userID || "anonymous"}`;
}

function buildUserSettings(state: RootState): UserSettings {
  return {
    darkMode: state.global.themeMode === "dark",
    autoSave: state.global.autoSave,
    onboarding: selectOnboardingProgress(state),
  };
}

let userSettingsSaveInFlight = false;
let userSettingsSaveGeneration = 0;
let pendingUserSettingsSave:
  | {
      token: string;
      settings: UserSettings;
      dispatch: Dispatch<AnyAction>;
      generation: number;
    }
  | undefined;

async function drainUserSettingsSaveQueue() {
  if (userSettingsSaveInFlight) {
    return;
  }

  userSettingsSaveInFlight = true;
  try {
    while (pendingUserSettingsSave) {
      const request = pendingUserSettingsSave;
      pendingUserSettingsSave = undefined;

      request.dispatch(setUserSettingsSaving());
      const saved = await UserSettingsService.PutUserSettings(
        request.token,
        request.settings
      );

      // Logout invalidates both queued and in-flight work. Do not repopulate
      // the cleared slice with a response from the previous user.
      if (request.generation !== userSettingsSaveGeneration) {
        continue;
      }

      request.dispatch(
        saved
          ? setUserSettingsSaveSucceeded(request.settings)
          : setUserSettingsSaveFailed()
      );
    }
  } finally {
    userSettingsSaveInFlight = false;
  }
}

function enqueueUserSettingsSave(state: RootState, dispatch: Dispatch<AnyAction>) {
  const token = state.authentication.jwt;
  if (
    !token ||
    !state.userSettings.isLoaded ||
    !state.onboarding.isHydrated
  ) {
    return;
  }

  pendingUserSettingsSave = {
    token,
    settings: buildUserSettings(state),
    dispatch,
    generation: userSettingsSaveGeneration,
  };
  void drainUserSettingsSaveQueue();
}

function cancelUserSettingsSaves(): void {
  pendingUserSettingsSave = undefined;
  userSettingsSaveGeneration += 1;
}

export const createPersistenceListenerMiddleware = () => {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: setShowCameraWidget,
    effect: (action) => {
      safeSetItem("showCameraWidget", action.payload.toString());
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setShowEnvironmentProps,
    effect: (action) => {
      safeSetItem("showEnvironmentProps", action.payload.toString());
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setThemeMode,
    effect: (action, listenerApi) => {
      safeSetItem("themeMode", action.payload);
      enqueueUserSettingsSave(
        listenerApi.getState() as RootState,
        listenerApi.dispatch
      );
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setBrowseDialogViewMode,
    effect: (action) => {
      safeSetItem(BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY, action.payload);
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setAutoConnectToUnity,
    effect: (action) => {
      safeSetItem("autoConnectToUnity", action.payload.toString());
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setAutoSave,
    effect: (action, listenerApi) => {
      safeSetItem("autoSave", action.payload.toString());
      enqueueUserSettingsSave(
        listenerApi.getState() as RootState,
        listenerApi.dispatch
      );
    },
  });

  listenerMiddleware.startListening({
    actionCreator: applyUserPreferences,
    effect: (action) => {
      if (typeof action.payload.darkMode === "boolean") {
        safeSetItem("themeMode", action.payload.darkMode ? "dark" : "light");
      }
      if (typeof action.payload.autoSave === "boolean") {
        safeSetItem("autoSave", action.payload.autoSave.toString());
      }
    },
  });

  // Onboarding progress shares the user-settings save queue with preferences.
  // setTourStarted, setTourStepIndex and setTourAborted are deliberately absent.
  [
    setTourCompleted,
    setTourAbandoned,
    setHintDismissed,
    setWelcomeChoice,
    setOnboardingReset,
  ].forEach((actionCreator) => {
    listenerMiddleware.startListening({
      actionCreator,
      effect: (_, listenerApi) => {
        enqueueUserSettingsSave(
          listenerApi.getState() as RootState,
          listenerApi.dispatch
        );
      },
    });
  });

  listenerMiddleware.startListening({
    actionCreator: setAuthenticationSuccess,
    effect: (_, listenerApi) => {
      const state = listenerApi.getState() as RootState;
      const authState = state.authentication;

      safeSetItem("jwt", authState.jwt);
      safeSetItem("userID", authState.userID);
      safeSetItem("email", authState.email);
      safeSetItem("refreshToken", authState.refreshToken);

      // The rollout intentionally starts fresh instead of uploading browser
      // progress. Server settings are hydrated later by the login hook.
      safeRemoveItem(legacyOnboardingStorageKey(authState.userID));
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setReauthenticationSuccess,
    effect: (_, listenerApi) => {
      const state = listenerApi.getState() as RootState;
      const authState = state.authentication;

      safeSetItem("jwt", authState.jwt);
      safeSetItem("userID", authState.userID);
      safeSetItem("refreshToken", authState.refreshToken);
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setLogout,
    effect: (_, listenerApi) => {
      cancelUserSettingsSaves();
      safeRemoveItem("jwt");
      safeRemoveItem("userID");
      safeRemoveItem("email");
      safeRemoveItem("refreshToken");
      listenerApi.dispatch(clearUserSettings());
      listenerApi.dispatch(setOnboardingCleared());
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setOrganizations,
    effect: (action, listenerApi) => {
      if (action.payload.length === 0) {
        return;
      }

      const cachedOrgId = safeGetItem("organizationId");
      if (cachedOrgId) {
        const cachedOrganization = action.payload.find(
          (organization) => organization.id === cachedOrgId
        );

        if (cachedOrganization) {
          const state = listenerApi.getState() as RootState;
          if (state.user.currentOrganization?.id !== cachedOrganization.id) {
            listenerApi.dispatch(setCurrentOrganization(cachedOrganization));
            return;
          }
        }
      }

      const state = listenerApi.getState() as RootState;
      const currentOrganization = state.user.currentOrganization;
      if (currentOrganization?.id) {
        safeSetItem("organizationId", currentOrganization.id);
      }
    },
  });

  listenerMiddleware.startListening({
    actionCreator: setCurrentOrganization,
    effect: (action) => {
      safeSetItem("organizationId", action.payload.id);
    },
  });

  return listenerMiddleware;
};

export const persistenceListenerMiddleware =
  createPersistenceListenerMiddleware();
